<app-side-dialog class="w-[700px]" [disableClose]="store.briefAnalyzeLoading$()">
  <div dialogHeader class="flex items-center gap-2">
    @if (step() === 'brief') {
      <button
        mat-icon-button
        type="button"
        class="mat-secondary-transparent-button-color mat-big-icon-button"
        data-testid="create-campaign-brief-back-button"
        [attr.aria-label]="'dialogs.createCampaignDialog.briefWizard.back' | translate"
        [disabled]="submitButtonLoading()"
        (click)="onBackToManualClick()"
      >
        <app-icon src="/icons/chevron-left.svg" class="w-6 h-6" />
      </button>
    }

    <h2>{{ title() }}</h2>
  </div>

  <div dialogContent>
    <form
      class="custom-form flex flex-col gap-10"
      [class.hidden]="step() === 'brief'"
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
    >
      <div class="flex flex-col gap-4">
        <mat-form-field>
          <input matInput formControlName="name" [placeholder]="'field.name' | translate" />
          <mat-error [control]="form.get('name')" />
        </mat-form-field>

        <app-file-uploader
          class="w-full"
          size="big"
          [imageWidth]="80"
          [imageHeight]="80"
          [title]="'dialogs.createCampaignDialog.selectCampaignIconOptional' | translate"
          [description]="'dialogs.createCampaignDialog.iconDescription' | translate"
          [imageId]="form.controls.iconId.value"
          [fileForm]="form.controls.imageFile"
          [requiredPermission]="[EPermissions.campaignCreate]"
          (imageSelected)="onImageSelected($event)"
          (imageRemoved)="onImageRemoved()"
          #fileUploaderComponent
        />
      </div>

      @if (showBriefUploader()) {
        <div class="flex items-center gap-3">
          <hr class="flex-1 border-shikenso-purple-base-12" />
          <span class="text-shikenso-purple-base-60 text-sm">
            {{ 'dialogs.createCampaignDialog.orLetAiSetItUp' | translate }}
          </span>
          <hr class="flex-1 border-shikenso-purple-base-12" />
        </div>

        <div
          class="flex items-center gap-4 rounded-2xl border border-dashed border-shikenso-pink-base bg-shikenso-pink-100-12 p-5"
          data-testid="create-campaign-ai-box"
          (dragover)="$event.preventDefault()"
          (drop)="onBriefBoxDrop($event)"
        >
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-shikenso-pink-base">
            <app-icon src="/icons/sparkles-2.svg" [size]="20" />
          </div>

          <div class="flex flex-1 flex-col gap-1">
            <span class="text-sm font-bold">{{ 'dialogs.createCampaignDialog.aiBox.title' | translate }}</span>
            <span class="text-xs text-shikenso-purple-base-60">
              {{ 'dialogs.createCampaignDialog.aiBox.description' | translate }}
            </span>
          </div>

          <div class="flex shrink-0 flex-col items-center gap-3">
            <button
              mat-flat-button
              type="button"
              color="secondary-white"
              data-testid="create-campaign-upload-briefing-button"
              [disabled]="submitButtonLoading()"
              (click)="onUploadBriefClick()"
            >
              {{ 'dialogs.createCampaignDialog.aiBox.uploadBriefing' | translate }}
            </button>

            <button
              mat-flat-button
              color="secondary-transparent"
              type="button"
              class="mat-micro-button"
              data-testid="create-campaign-describe-briefing-button"
              [disabled]="submitButtonLoading()"
              (click)="onDescribeBriefClick()"
            >
              <span class="text-xs underline">{{ 'dialogs.createCampaignDialog.aiBox.describeIt' | translate }}</span>
            </button>
          </div>
        </div>
      }

      <div class="flex justify-end gap-3">
        <button
          mat-flat-button
          color="secondary-transparent"
          type="button"
          [disabled]="store.campaignSubmitLoading$() || submitButtonLoading()"
          (click)="onCloseClick()"
        >
          {{ 'shared.cancel' | translate }}
        </button>

        <button
          mat-flat-button
          type="submit"
          [disabled]="!form.valid || store.campaignSubmitLoading$() || submitButtonLoading()"
        >
          <p [class.hidden]="submitButtonLoading()">{{ submitButtonText() }}</p>

          @if (submitButtonLoading()) {
            <app-dots-spinner />
          }
        </button>
      </div>
    </form>

    @if (showBriefUploader()) {
      @let isBriefFileLimitReached = briefForm.controls.files.length >= briefMaxFiles;

      <div
        class="custom-form flex flex-col gap-6"
        data-testid="create-campaign-brief-step"
        [class.hidden]="step() === 'manual'"
      >
        <div class="flex flex-col gap-2">
          <span class="text-sm font-bold">{{ 'dialogs.createCampaignDialog.briefStep.briefing' | translate }}</span>

          <app-file-uploader
            #briefFileUploader
            class="w-full"
            size="big"
            data-testid="brief-file-uploader"
            [imageWidth]="48"
            [imageHeight]="48"
            [title]="
              isBriefFileLimitReached
                ? ('dialogs.createCampaignDialog.briefStep.maxFilesReached' | translate: { maxFiles: briefMaxFiles })
                : ('dialogs.createCampaignDialog.briefStep.uploadTitle' | translate)
            "
            [description]="
              'dialogs.createCampaignDialog.briefStep.uploadDescription' | translate: { maxFiles: briefMaxFiles }
            "
            [fileExtensions]="briefFileExtensions"
            [fileForm]="briefUploadControl"
            [clearAfterUpload]="true"
            [uploadRequest]="briefFileUploadStub"
            [disabled]="isBriefFileLimitReached"
            [requiredPermission]="[EPermissions.campaignCreate]"
            (imageSelected)="onBriefFileSelected($event)"
          />

          @if (briefForm.controls.files.length) {
            <div class="mt-3 flex flex-col">
              @for (fileControl of briefForm.controls.files.controls; track fileControl; let index = $index) {
                <app-file-uploader
                  displayMode="list"
                  data-testid="brief-file-row"
                  [fileForm]="fileControl"
                  (imageRemoved)="onBriefFileRemoved(index)"
                />
              }
            </div>
          }
        </div>

        <div class="flex flex-col gap-2">
          <label for="create-campaign-brief-prompt" class="text-sm font-bold">
            {{ 'dialogs.createCampaignDialog.briefStep.instructions' | translate }}
          </label>

          <mat-form-field class="expandable-textarea-field w-full">
            <textarea
              #briefPromptInput
              id="create-campaign-brief-prompt"
              matInput
              cdkTextareaAutosize
              cdkAutosizeMinRows="5"
              cdkAutosizeMaxRows="10"
              data-testid="brief-prompt-input"
              [formControl]="briefForm.controls.prompt"
              [placeholder]="'dialogs.createCampaignDialog.briefStep.instructionsPlaceholder' | translate"
            ></textarea>
            <mat-error [control]="briefForm.controls.prompt" />
          </mat-form-field>
        </div>

        <div
          class="flex flex-col gap-3 rounded-2xl border border-shikenso-pink-100-50 bg-shikenso-pink-100-12 p-4"
          data-testid="brief-ai-will-draft"
        >
          <span class="text-sm font-bold">{{ 'dialogs.createCampaignDialog.briefStep.aiWillDraft' | translate }}</span>

          <div class="flex flex-wrap gap-2">
            @for (chipKey of briefDraftChipKeys; track chipKey) {
              <span class="rounded-full border border-shikenso-pink-100-50 bg-shikenso-base-white px-3 py-1 text-xs">
                {{ chipKey | translate }}
              </span>
            }
          </div>
        </div>

        @if (briefSubmitAttempted() && briefForm.hasError('briefSourceRequired')) {
          <p class="text-xs text-shikenso-error-base" role="alert" data-testid="brief-source-required-error">
            {{ 'dialogs.createCampaignDialog.briefStep.sourceRequired' | translate }}
          </p>
        }

        <div class="flex items-center justify-between gap-3">
          <button
            mat-flat-button
            color="secondary-transparent"
            type="button"
            data-testid="create-campaign-set-up-manually-button"
            [disabled]="submitButtonLoading()"
            (click)="onBackToManualClick()"
          >
            <span class="underline">{{ 'dialogs.createCampaignDialog.briefStep.setUpManually' | translate }}</span>
          </button>

          <div class="flex gap-3">
            <button
              mat-flat-button
              color="secondary-transparent"
              type="button"
              [disabled]="submitButtonLoading()"
              (click)="onCloseClick()"
            >
              {{ 'shared.cancel' | translate }}
            </button>

            <button
              mat-flat-button
              type="button"
              data-testid="brief-generate-draft-button"
              [disabled]="submitButtonLoading()"
              (click)="onGenerateDraftClick()"
            >
              <span class="flex items-center gap-2" [class.hidden]="submitButtonLoading()">
                <app-icon src="/icons/sparkles-2.svg" [size]="16" />
                {{ 'dialogs.createCampaignDialog.briefStep.generateDraft' | translate }}
              </span>

              @if (submitButtonLoading()) {
                <app-dots-spinner />
              }
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</app-side-dialog>

<ng-template #briefAnalyzingOverlay>
  <div
    class="fixed inset-0 flex items-center justify-center bg-black/40"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'dialogs.createCampaignDialog.analyzingBrief' | translate"
    cdkTrapFocus
    cdkTrapFocusAutoCapture
  >
    <div class="relative flex flex-col items-center gap-5 bg-shikenso-base-white rounded-[24px] px-14 py-10">
      <button
        type="button"
        class="absolute top-4 right-4"
        (click)="onCancelBriefAnalyze()"
        [attr.aria-label]="'shared.close' | translate"
      >
        <app-icon src="/icons/close.svg" [size]="20" />
      </button>

      <p class="text-shikenso-purple-base text-xl font-medium text-center max-w-xs">
        {{ 'dialogs.createCampaignDialog.analyzingBrief' | translate }}
      </p>

      <app-dots-spinner />
    </div>
  </div>
</ng-template>
